import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { BRAND_LUXE_PRIMARY, BRAND_SURFACE_CONTAINER } from './brandDashboardTokens';

export interface SummaryStat {
    icon?: string;
    label: string;
}

export interface BrandCampaignStepFooterProps {
    onBack?: () => void;
    onNext: () => void;
    /** Called when the user clicks the Next button while it is disabled — use to trigger field validation highlights. */
    onNextAttempt?: () => void;
    nextLabel: string;
    backLabel?: string;
    disableNext?: boolean;
    summaryStats?: SummaryStat[];
    summaryBudgetLabel?: string;
    summaryBudgetValue?: string;
    /** Shown under the total (e.g. tier × participants + fees). */
    summaryBudgetDetail?: React.ReactNode;
    /** Persist in-progress wizard fields to local storage. */
    onSaveDraft?: () => void;
    draftSavedHint?: string | null;
}

export function BrandCampaignStepFooter({
    onBack,
    onNext,
    onNextAttempt,
    nextLabel,
    backLabel = 'Back',
    disableNext,
    summaryStats,
    summaryBudgetLabel,
    summaryBudgetValue,
    summaryBudgetDetail,
    onSaveDraft,
    draftSavedHint,
}: BrandCampaignStepFooterProps) {
    return (
        <Box
            id="campaign-step-footer"
            sx={{
                bgcolor: `${BRAND_SURFACE_CONTAINER}99`,
                backdropFilter: 'blur(20px)',
                px: { xs: 2, sm: 3 },
                py: { xs: 2.5, sm: 4 },
                borderRadius: '20px 20px 0 0',
                mt: 4,
                width: '100%',
                minWidth: 0,
            }}>
            {onSaveDraft && (
                <Box sx={{ mb: 2 }}>
                    <Button
                        onClick={onSaveDraft}
                        sx={{
                            alignSelf: 'flex-start',
                            p: 0,
                            minWidth: 0,
                            color: 'rgba(255,255,255,0.55)',
                            fontSize: 11,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            letterSpacing: '0.16em',
                            textTransform: 'uppercase',
                            '&:hover': { color: BRAND_LUXE_PRIMARY, bgcolor: 'transparent' },
                        }}>
                        Save draft
                    </Button>
                    {draftSavedHint && (
                        <Typography
                            sx={{
                                fontSize: 10,
                                fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                color: BRAND_LUXE_PRIMARY,
                                mt: 0.5,
                                letterSpacing: '0.06em',
                            }}>
                            {draftSavedHint}
                        </Typography>
                    )}
                </Box>
            )}
            {(summaryStats || summaryBudgetValue || summaryBudgetDetail) && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: { xs: 'flex-start', sm: 'flex-end' },
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'space-between',
                        gap: 2,
                        mb: 3,
                    }}>
                    <Box
                        sx={{
                            flex: summaryBudgetValue || summaryBudgetDetail ? undefined : 1,
                            minWidth: 0,
                            width: { xs: '100%', sm: summaryBudgetValue || summaryBudgetDetail ? 'auto' : '100%' },
                        }}>
                        <Typography
                            sx={{
                                fontSize: 10,
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                textTransform: 'uppercase',
                                letterSpacing: '0.2em',
                                color: 'rgba(255,255,255,0.5)',
                                mb: 1,
                            }}>
                            Campaign Summary
                        </Typography>
                        {summaryStats && (
                            <Box sx={{ display: 'flex', gap: 1.5, width: '100%' }}>
                                {summaryStats.map(stat => (
                                    <Typography
                                        key={stat.label}
                                        sx={{
                                            fontSize: 12,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                            color: '#fff',
                                            bgcolor: '#262626',
                                            px: 1.5,
                                            py: 0.5,
                                            borderRadius: 999,
                                            flex:
                                                !summaryBudgetValue && !summaryBudgetDetail && summaryStats.length === 1
                                                    ? 1
                                                    : undefined,
                                            textAlign:
                                                !summaryBudgetValue && !summaryBudgetDetail && summaryStats.length === 1
                                                    ? 'center'
                                                    : undefined,
                                        }}>
                                        {stat.label}
                                    </Typography>
                                ))}
                            </Box>
                        )}
                    </Box>
                    {(summaryBudgetValue || summaryBudgetDetail) && (
                        <Box sx={{ textAlign: 'right', maxWidth: { xs: '100%', sm: 390 }, minWidth: 0 }}>
                            {summaryBudgetValue && (
                                <>
                                    <Typography
                                        sx={{
                                            fontSize: 10,
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.2em',
                                            color: BRAND_LUXE_PRIMARY,
                                            mb: 0.5,
                                        }}>
                                        {summaryBudgetLabel ?? 'Total Budget'}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: FONT_ENUM.MAIN_BOLD,
                                            fontWeight: 900,
                                            fontSize: '1.75rem',
                                            color: '#fff',
                                            letterSpacing: '-0.02em',
                                        }}>
                                        {summaryBudgetValue}
                                    </Typography>
                                </>
                            )}
                            {summaryBudgetDetail}
                        </Box>
                    )}
                </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                {onBack && (
                    <Button
                        onClick={onBack}
                        sx={{
                            flex: 1,
                            py: 2,
                            borderRadius: 999,
                            border: '1px solid rgba(255,255,255,0.12)',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            color: '#fff',
                            textTransform: 'none',
                            fontSize: 15,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' },
                        }}>
                        {backLabel}
                    </Button>
                )}
                <Button
                    onClick={() => {
                        if (disableNext) {
                            onNextAttempt?.();
                        } else {
                            onNext();
                        }
                    }}
                    sx={{
                        flex: { xs: 1, sm: 2 },
                        py: 2,
                        borderRadius: 999,
                        background: `linear-gradient(135deg, ${BRAND_LUXE_PRIMARY}, #feb700)`,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontWeight: 800,
                        color: '#392700',
                        textTransform: 'none',
                        fontSize: 15,
                        boxShadow: '0 10px 20px rgba(255,193,7,0.2)',
                        '&:hover': { boxShadow: '0 15px 30px rgba(255,193,7,0.3)' },
                        ...(disableNext ? { opacity: 0.5, cursor: 'default' } : {}),
                    }}>
                    {nextLabel}
                </Button>
            </Box>
        </Box>
    );
}
